import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Badge, Button, EmptyState, SectionHeader } from "../../../components/ui";
import CentinelaWatchPill from "../components/CentinelaWatchPill";
import ProtocolFormModal from "../components/ProtocolFormModal";
import { buildProtocols } from "../services/centinelaMocks";
import type { Protocol } from "../types";

export default function ProtocolosSection() {
  const { t } = useTranslation("common");

  const [custom, setCustom] = useState<Protocol[]>([]);
  const [showFormModal, setShowFormModal] = useState(false);

  const seeded = useMemo(() => buildProtocols(t), [t]);
  const protocols = [...seeded, ...custom];

  const toggleProtocol = (id: string) => {
    setCustom((prev) =>
      prev.map((protocol) =>
        protocol.id === id ? { ...protocol, enabled: !protocol.enabled } : protocol,
      ),
    );
    toast.success(t("monitoreoIa.protocols.toggled"));
  };

  const createProtocol = (protocol: Protocol) => {
    setCustom((prev) => [...prev, protocol]);
    setShowFormModal(false);
    toast.success(t("monitoreoIa.protocols.created", { name: protocol.name }));
  };

  return (
    <div className="flex h-full w-full flex-col">
      <SectionHeader
        eyebrow={<CentinelaWatchPill />}
        title={t("monitoreoIa.protocols.title")}
        subtitle={t("monitoreoIa.protocols.subtitle")}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="neutral" size="sm">
              {t("monitoreoIa.config.notPersisted")}
            </Badge>
            <Button
              size="sm"
              onClick={() => setShowFormModal(true)}
              icon={<i className="ri-add-line" />}
            >
              {t("monitoreoIa.protocols.new")}
            </Button>
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto p-3 sm:p-6">
        <div className="mx-auto w-full max-w-6xl">
          {protocols.length === 0 ? (
            <EmptyState
              icon="ri-file-list-3-line"
              title={t("monitoreoIa.protocols.empty")}
              description={t("monitoreoIa.protocols.emptyDescription")}
              action={
                <Button onClick={() => setShowFormModal(true)} icon={<i className="ri-add-line" />}>
                  {t("monitoreoIa.protocols.new")}
                </Button>
              }
            />
          ) : (
            <ul className="grid gap-4 md:grid-cols-2">
              {protocols.map((protocol) => {
                const isCustom = custom.some((item) => item.id === protocol.id);

                return (
                  <li key={protocol.id} className="rounded-xl border border-line bg-card p-4">
                    <div className="flex items-center gap-2">
                      <span
                        className={`h-2 w-2 shrink-0 rounded-full ${
                          protocol.enabled ? "bg-success" : "bg-content-muted"
                        }`}
                      />
                      <span className="min-w-0 flex-1 truncate text-[13.5px] font-bold text-content">
                        {protocol.name}
                      </span>
                      {isCustom && (
                        <Button
                          size="xs"
                          variant="ghost"
                          className="shrink-0"
                          onClick={() => toggleProtocol(protocol.id)}
                        >
                          {protocol.enabled
                            ? t("monitoreoIa.protocols.disable")
                            : t("monitoreoIa.protocols.enable")}
                        </Button>
                      )}
                    </div>

                    <p className="mb-3 mt-1 text-[11.5px] text-content-secondary">
                      {protocol.trigger}
                    </p>

                    <ol className="flex flex-wrap items-center gap-1.5">
                      {protocol.steps.map((step, index) => (
                        <li key={step.id} className="flex items-center gap-1.5">
                          <span className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-app px-2.5 py-1 text-[11px] text-content-secondary">
                            <span className="font-medium text-warning">{step.delay}</span>
                            {t(`monitoreoIa.protocolActions.${step.actionId}`)}
                          </span>
                          {index < protocol.steps.length - 1 && (
                            <i className="ri-arrow-right-line text-[11px] text-content-faint" />
                          )}
                        </li>
                      ))}
                    </ol>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {showFormModal ? (
        <ProtocolFormModal onClose={() => setShowFormModal(false)} onCreate={createProtocol} />
      ) : null}
    </div>
  );
}
